import { ChangeEvent, FormEvent, useState } from 'react';

import { useGeoValue } from '@entities/geo';
import { TOrder, useApproveOrderARSMutation, useApproveOrderMutation } from '@entities/orders';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { FormItemWithError } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';
import { toPlainAmount } from '@shared/utils/formatAmount';

import { IOrderModalContextProps } from '../model/types';

interface IProps<T> {
  order: T;
  refetch: () => void;
}

const validateUTR = (value: string): boolean => /^(\d{12})(\**)$/.test(value); // 12 digits, optionally followed by asterisks (*)
// Light-touch client-side guard for the ARS approve fields: letters, digits, - and _ only —
// the charset the backend actually accepts. The backend stays the source of truth for the
// exact format (it validates and stores the value); this just stops the FE from accepting
// characters the backend will reject (e.g. spaces, . and /).
const validateBasicValue = (value: string): boolean => /^[A-Za-z0-9_-]+$/.test(value);

const ALLOWED_CHARS_HINT = 'may only contain letters, numbers, hyphens (-) and underscores (_)';

const VALIDATION_ERRORS = {
  customer_transaction_id: 'UTR must be exactly 12 digits, optionally followed by asterisks (*)',
  cuit_cuil: `Cuit/Cuil ${ALLOWED_CHARS_HINT}`,
  coelsa_id: `Coelsa ID ${ALLOWED_CHARS_HINT}`,
} as const;

const createValidationError = (
  messageKey: keyof typeof VALIDATION_ERRORS,
  displayField: string = messageKey,
): FetchBaseQueryError => ({
  status: 422,
  data: { errors: { [displayField]: [VALIDATION_ERRORS[messageKey]] } },
});

const computeArsFieldError = (
  value: string,
  messageKey: 'cuit_cuil' | 'coelsa_id',
): FetchBaseQueryError | null =>
  value && !validateBasicValue(value) ? createValidationError(messageKey) : null;

export const ApproveOrder = <T extends TOrder>({ order, refetch }: IProps<T>) => {
  const { requested_amount, actual_amount, id, customer_transaction_id, status_reason } = order;

  const geo = useGeoValue();
  const isArs = geo === 'ars';

  const [actualAmount, setActualAmount] = useState(actual_amount || requested_amount);
  const [customerTransactionId, setCustomerTransactionId] = useState(customer_transaction_id || '');
  const [reason, setReason] = useState(status_reason);
  const [cuitCuil, setCuitCuil] = useState('');
  const [coelsaId, setCoelsaId] = useState('');

  const [approveOrder, { isLoading, isError, error, isSuccess }] = useApproveOrderMutation();
  const [approveOrderARS, { isLoading: isLoadingARS, isError: isErrorARS, error: errorARS, isSuccess: isSuccessARS }] = useApproveOrderARSMutation();

  const currentIsLoading = isArs ? isLoadingARS : isLoading;
  const currentIsError = isArs ? isErrorARS : isError;
  const currentError = isArs ? errorARS : error;
  const currentIsSuccess = isArs ? isSuccessARS : isSuccess;

  const [utrError, setUtrError] = useState<FetchBaseQueryError | null>(null);
  const [cuitCuilError, setCuitCuilError] = useState<FetchBaseQueryError | null>(null);
  const [coelsaIdError, setCoelsaIdError] = useState<FetchBaseQueryError | null>(null);

  const {
    closeModal,
    additionalContext: { setSelectedIds },
  } = useModal<IOrderModalContextProps>();

  const handleChangeCustomerTransactionId = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerTransactionId(value);
    setUtrError(
      value && !validateUTR(value) ? createValidationError('customer_transaction_id') : null,
    );
  };

  const handleChangeCuitCuil = (e: ChangeEvent<HTMLInputElement>) => {
    setCuitCuil(e.target.value);
    setCuitCuilError(computeArsFieldError(e.target.value, 'cuit_cuil'));
  };

  const handleChangeCoelsaId = (e: ChangeEvent<HTMLInputElement>) => {
    setCoelsaId(e.target.value);
    setCoelsaIdError(computeArsFieldError(e.target.value, 'coelsa_id'));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedCuitCuil = cuitCuil.trim();
    const trimmedCoelsaId = coelsaId.trim();

    if (!isArs) {
      const utrError = customerTransactionId && !validateUTR(customerTransactionId)
        ? createValidationError('customer_transaction_id')
        : null;
      setUtrError(utrError);
      if (utrError) return;
    } else {
      // Keep the inputs in sync with what is actually sent (e.g. whitespace-only becomes empty)
      setCuitCuil(trimmedCuitCuil);
      setCoelsaId(trimmedCoelsaId);
      const cuitCuilErr = computeArsFieldError(trimmedCuitCuil, 'cuit_cuil');
      const coelsaIdErr = computeArsFieldError(trimmedCoelsaId, 'coelsa_id');
      setCuitCuilError(cuitCuilErr);
      setCoelsaIdError(coelsaIdErr);
      if (cuitCuilErr || coelsaIdErr) return;
    }

    try {
      const result = isArs
        ? await approveOrderARS({ id, actual_amount: actualAmount, cuit_cuil: trimmedCuitCuil || null, coelsa_id: trimmedCoelsaId || null, reason })
        : await approveOrder({ id, actual_amount: actualAmount, customer_transaction_id: customerTransactionId, reason });

      if ('data' in result) {
        setSelectedIds([]);
        closeModal();
        refetch();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <h2>Approve Order</h2>

      <form className='popup-form' onSubmit={handleSubmit}>
        <FormItemWithError
          name='actual_amount'
          label='Actual Amount'
          isError={currentIsError}
          error={currentError}
          secondName='order'
        >
          <input
            type='text'
            name='actual_amount'
            id='actual_amount'
            defaultValue={toPlainAmount(actualAmount)}
            onChange={e => setActualAmount(e.target.value)}
          />
        </FormItemWithError>

        {!isArs && (
          <FormItemWithError
            name='customer_transaction_id'
            label='UTR'
            isError={Boolean(utrError) || currentIsError}
            error={utrError || currentError}
            secondName='order'
          >
            <input
              type='text'
              name='customer_transaction_id'
              id='customer_transaction_id'
              defaultValue={customerTransactionId}
              onChange={handleChangeCustomerTransactionId}
            />
          </FormItemWithError>
        )}

        {isArs && (
          <>
            <FormItemWithError
              name='cuit_cuil'
              label='Cuit/Cuil'
              isError={Boolean(cuitCuilError) || currentIsError}
              error={cuitCuilError || currentError}
              secondName='order'
            >
              <input
                type='text'
                name='cuit_cuil'
                id='cuit_cuil'
                value={cuitCuil}
                onChange={handleChangeCuitCuil}
                placeholder='XX-XXXXXXXX-X or 11 digits'
              />
            </FormItemWithError>

            <FormItemWithError
              name='coelsa_id'
              label='Coelsa ID'
              isError={Boolean(coelsaIdError) || currentIsError}
              error={coelsaIdError || currentError}
              secondName='order'
            >
              <input
                type='text'
                name='coelsa_id'
                id='coelsa_id'
                value={coelsaId}
                onChange={handleChangeCoelsaId}
                placeholder='24 alphanumeric characters'
              />
            </FormItemWithError>
          </>
        )}

        <FormItemWithError
          secondName='order'
          name='reason'
          label='Reason'
          isError={currentIsError}
          error={currentError}
        >
          <input
            type='text'
            name='reason'
            id='reason'
            defaultValue={reason}
            onChange={e => setReason(e.target.value)}
          />
        </FormItemWithError>

        {currentIsSuccess && <div className='form-success'>Successfully approved</div>}

        <div className='form-submit'>
          <button type='submit' disabled={currentIsLoading}>
            Approve
          </button>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};
